import { defineMessages, useIntl } from 'react-intl';

import { useFormattedPriceWithTax } from '@/hooks/purchase/useFormattedPriceWithTax';
import { usePricesState, usePurchaseParamsState } from '@/hooks/store';
import genericMessages from '@/messages/messages';
import { getPrice } from '@/store/purchaseParams/types';

const messages = defineMessages({
	discountLifetimeSubheading: {
		id: 'redesign.purchase.purchaseForm.lifetime.priceSubtitle',
		defaultMessage: 'One time payment of {formattedPrice}',
		description: 'Subheading with discount for subscribe form with lifetime coupon',
	},
});

interface UseDiscountPricing {
	(): {
		discountHeading: string | null;
		discountSubheading: string | null;
	};
}

export const useDiscountPricing: UseDiscountPricing = () => {
	const purchaseParams = usePurchaseParamsState();
	const prices = usePricesState();
	const { formatMessage } = useIntl();
	const purchaseType = purchaseParams?.purchaseType?.type;

	const priceWithTax = useFormattedPriceWithTax(getPrice(purchaseParams?.plan, prices.current));

	const getHeading = (): string | null => {
		if (purchaseType === 'monthly' && prices?.coupons?.monthly?.percent_off) {
			return formatMessage(genericMessages.getPercentOff, {
				percent: prices?.coupons?.monthly?.percent_off,
			});
		}

		if (
			(purchaseType === 'subscribe' && prices?.coupons?.yearly?.percent_off) ||
			(purchaseType === 'freetrial' && prices?.coupons?.yearly?.percent_off)
		) {
			return formatMessage(genericMessages.getPercentOff, {
				percent: prices?.coupons?.yearly?.percent_off,
			});
		}

		if (purchaseType === 'lifetime' && prices?.coupons?.lifetime?.percent_off) {
			return formatMessage(genericMessages.getPercentOff, {
				percent: prices?.coupons?.lifetime?.percent_off,
			});
		}

		return null;
	};

	const getSubheading = (): string | null => {
		// Monthly plan type
		if (purchaseType === 'monthly') {
			if (prices?.coupons?.monthly?.percent_off) {
				return formatMessage(genericMessages.purchaseTitleMonthly);
			}
		}

		// Yearly plan type
		if (purchaseType === 'subscribe' || purchaseType === 'freetrial') {
			if (prices?.coupons?.yearly?.percent_off) {
				return formatMessage(genericMessages.purchaseTitleYearly);
			}
		}

		if (purchaseType === 'lifetime') {
			if (prices?.coupons?.lifetime?.percent_off) {
				return formatMessage(messages.discountLifetimeSubheading, {
					formattedPrice: priceWithTax,
				});
			}
		}

		return null;
	};

	return {
		discountHeading: getHeading(),
		discountSubheading: getSubheading(),
	};
};
